import './LoginForm.css'

import {type CSSProperties, useCallback, useRef, useState} from 'react'
import {type ComAtprotoServerDescribeServer} from '@atproto/api'
import {XRPCError} from '@atproto/xrpc'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useEventCallback} from 'usehooks-ts'

import {useRequestNotificationsPermission} from '#/lib/notifications/notifications'
import {ROUTES} from '#/lib/routes/constants.ts'
import {cleanError} from '#/lib/strings/errors'
import {logger} from '#/logger'
import {useSetHasCheckedForStarterPack} from '#/state/preferences/used-starter-packs'
import {useAgent, useSessionApi} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {QrSessionBlock} from '#/screens/SignUpWizard/QrSessionBlock.tsx'
import {FormError} from '#/components/forms/FormError'
import LockIcon from '#/components/icons/LockIcon'
import MailIcon from '#/components/icons/MailIcon'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {TOKENS} from '#/wsocial/service/design-system/tokens'
// import {extractQrTextFromImageUrl} from '#/wsocial/utils/qrCode'
import logoSvg from '../../../assets/icon-wsocial.svg'
import {type QrSessionStatusResult} from '../SignUpWizard/useQrSession'

type ServiceDescription = ComAtprotoServerDescribeServer.OutputSchema

const T = {
  inputBg: '#272F44',
  textMedium: '#D0CFD7',
  textPlaceholder: '#9997A7',
  accentHover: TOKENS.dark.custom_accent_hover,
} as const

export const LoginFormDesktop = ({
  error,
  serviceUrl,
  setError,
  onPressForgotPassword,
  onAttemptSuccess,
  onAttemptFailed,
}: {
  error: string
  serviceUrl: string
  serviceDescription: ServiceDescription | undefined
  initialHandle: string
  setError: (v: string) => void
  setServiceUrl: (v: string) => void
  onPressRetryConnect: () => void
  onPressBack: () => void
  onPressForgotPassword: () => void
  onAttemptSuccess: () => void
  onAttemptFailed: () => void
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authFactorToken, setAuthFactorToken] = useState('')
  const [isAuthFactorTokenNeeded, setIsAuthFactorTokenNeeded] = useState(false)
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const authFactorRef = useRef<HTMLInputElement>(null)
  // const [signTagUrl, setSignTagUrl] = useState('')
  const {_} = useLingui()
  const {login, resumeSession} = useSessionApi()
  const requestNotificationsPermission = useRequestNotificationsPermission()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const setHasCheckedForStarterPack = useSetHasCheckedForStarterPack()
  const agent = useAgent()
  const ss = useSemanticTokens()
  const onComplete: (data: QrSessionStatusResult) => Promise<void> =
    useEventCallback(async (data): Promise<void> => {
      if (data.result) {
        try {
          const switched = await resumeSession({
            service: serviceUrl,
            ...data.result,
            active: true,
          })
          if (!switched) {
            throw new Error('Session switch was superseded')
          }
          onAttemptSuccess()
          setShowLoggedOut(false)
          setHasCheckedForStarterPack(true)
          void requestNotificationsPermission('Login')
        } catch (e) {
          logger.error('Quick Login session resume failed', {error: e})
          setError(_(msg`Login failed. Please try again.`))
        }
      }
    })

  const onSubmit = useCallback(async () => {
    // Fall back to DOM values to handle browser autofill (Chrome delays
    // applying autofill values to state until after a user gesture)
    const emailValue = email || emailRef.current?.value || ''
    const passwordValue = password || passwordRef.current?.value || ''
    if (isProcessing || !emailValue || !passwordValue) return
    setError('')
    setIsProcessing(true)
    try {
      await login(
        {
          service: serviceUrl,
          identifier: emailValue,
          password: passwordValue,
          authFactorToken: authFactorToken.trim() || undefined,
        },
        'LoginForm',
      )
      onAttemptSuccess()
      setShowLoggedOut(false)
      setHasCheckedForStarterPack(true)
      void requestNotificationsPermission('Login')
    } catch (err: unknown) {
      if (err instanceof XRPCError && err.error === 'AuthFactorTokenRequired') {
        setIsAuthFactorTokenNeeded(true)
        setError('')
      } else {
        onAttemptFailed()
        setError(cleanError(err))
      }
    } finally {
      setIsProcessing(false)
    }
  }, [
    isProcessing,
    email,
    password,
    authFactorToken,
    login,
    serviceUrl,
    onAttemptSuccess,
    onAttemptFailed,
    setError,
    setShowLoggedOut,
    setHasCheckedForStarterPack,
    requestNotificationsPermission,
  ])

  const inputStyle: CSSProperties = {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: 15,
    fontWeight: 400,
    color: '#fff',
    lineHeight: '23px',
    minWidth: 0,
  }

  const inputRowStyle: CSSProperties = {
    backgroundColor: T.inputBg,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    height: 44,
    padding: '0 12px',
    borderRadius: 10,
  }

  const labelStyle: CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '17px',
    color: T.textMedium,
    display: 'block',
    margin: 0,
  }

  const canSubmit =
    !isProcessing && (!isAuthFactorTokenNeeded || !!authFactorToken.trim())

  return (
    <div
      className="LoginPageShell"
      style={{
        backgroundColor: ss.bg.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: '100vh',
      }}>
      <div
        className="LoginPageShellInner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100%',
        }}>
        {/* Logo */}
        <div
          className="LoginLogoBlock"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <img src={logoSvg} alt="W Social" style={{width: 80, height: 80}} />
        </div>

        {/* Heading */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '0 16px',
            textAlign: 'center',
          }}>
          <h1
            style={{
              fontSize: 30,
              fontWeight: 700,
              lineHeight: '35px',
              color: '#fff',
              margin: 0,
            }}>
            <Trans>Sign in</Trans>
          </h1>
          <p
            style={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '17px',
              color: T.textMedium,
              margin: 0,
            }}>
            <Trans>Scan the code, or use email and password.</Trans>
          </p>
        </div>

        {/* Form */}
        <div
          style={{
            display: 'flex',
            gap: 28,
            padding: '24px 16px',
            alignItems: 'flex-start',
          }}>
          {/* QR Code - Left */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px 0',
              gap: 12,
              flexShrink: 0,
            }}>
            <QrSessionBlock
              size={300}
              queryKey="wid-login-qr-code"
              initFn={async (signal, prevServiceId, prevSessionId) => {
                const data = await agent.quicklogin.init(
                  {
                    allowCreate: !prevServiceId,
                    ...(prevServiceId && {serviceId: prevServiceId}),
                    ...(prevSessionId && {sessionId: prevSessionId}),
                  },
                  {signal},
                )
                /*if (data.qrCodeUrl) {
                  try {
                    const qrText = await extractQrTextFromImageUrl(data.qrCodeUrl)
                    // if (qrText) setSignTagUrl(qrText)
                  } catch (e) {
                    logger.error('Error extracting QR code url from image')
                  }
                }*/
                return data
              }}
              statusFn={(params, signal) =>
                agent.quicklogin.status(params, {signal})
              }
              onCompleted={e => void onComplete(e)}
              autoRefresh
            />
          </div>

          {/* Form fields - Right */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              minWidth: 0,
              alignSelf: 'center',
              paddingTop: 8,
              paddingBottom: 32,
            }}>
            {/* Email */}
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
              <label style={labelStyle}>
                <Trans>Email</Trans>
              </label>
              <div style={inputRowStyle}>
                <MailIcon />
                <input
                  ref={emailRef}
                  type="email"
                  placeholder={_(msg`Your email`)}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onAnimationStart={e => {
                    if (e.animationName === 'onAutoFillStart')
                      setEmail(e.currentTarget.value)
                  }}
                  disabled={isProcessing}
                  autoComplete="email"
                  className="SignInInput"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Password + Continue */}
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'flex-end',
              }}>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}>
                <label style={labelStyle}>
                  <Trans>Password</Trans>
                </label>
                <div style={inputRowStyle}>
                  <LockIcon />
                  <input
                    ref={passwordRef}
                    type="password"
                    placeholder={_(msg`Your password`)}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && void onSubmit()}
                    onAnimationStart={e => {
                      if (e.animationName === 'onAutoFillStart')
                        setPassword(e.currentTarget.value)
                    }}
                    disabled={isProcessing}
                    autoComplete="current-password"
                    className="SignInInput"
                    style={inputStyle}
                  />
                </div>
              </div>
              <button
                onClick={() => void onSubmit()}
                disabled={!canSubmit}
                className="SignInContinueBtn"
                style={{
                  backgroundColor: ss.button.primary.bg,
                  color: ss.button.primary.text,
                  fontSize: 15,
                  fontWeight: 500,
                  lineHeight: '20px',
                  padding: '0 25px',
                  height: 44,
                  borderRadius: 8,
                  border: 'none',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  opacity: canSubmit ? 1 : 0.6,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}>
                {isProcessing ? '...' : <Trans>Sign in</Trans>}
              </button>
            </div>

            {isAuthFactorTokenNeeded && (
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                <label style={labelStyle}>
                  <Trans>2FA confirmation</Trans>
                </label>
                <p
                  style={{
                    fontSize: 13,
                    color: T.textMedium,
                    margin: 0,
                    lineHeight: '17px',
                  }}>
                  <Trans>
                    A sign in code has been sent to your email address.
                  </Trans>
                </p>
                <div style={inputRowStyle}>
                  <LockIcon />
                  <input
                    ref={authFactorRef}
                    type="text"
                    autoComplete="one-time-code"
                    placeholder={_(msg`Confirmation code`)}
                    value={authFactorToken}
                    onChange={e =>
                      setAuthFactorToken(e.target.value.toUpperCase())
                    }
                    onKeyDown={e => e.key === 'Enter' && void onSubmit()}
                    disabled={isProcessing}
                    autoFocus
                    className="SignInInput"
                    style={{
                      ...inputStyle,
                      textTransform: authFactorToken ? 'uppercase' : 'none',
                    }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={onPressForgotPassword}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 400,
                color: T.textMedium,
                padding: 0,
                textAlign: 'left',
              }}>
              <Trans>Forgot password?</Trans>
            </button>

            <FormError error={error} />
          </div>
        </div>

        {/* Bottom footer */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            padding: '16px 16px',
          }}>
          <div
            style={{
              height: 1,
              backgroundColor: ss.border.contrast_low,
              width: '100%',
            }}
          />
          <p
            style={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '17px',
              color: T.textMedium,
              margin: 0,
            }}>
            <Trans>
              If you don't have an account or haven't received an invitation,
              please
            </Trans>{' '}
            <a href={ROUTES.SIGNUP} className="LinkNew">
              <Trans>join the waitlist</Trans>
            </a>
            .
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            padding: '16px 16px',
          }}>
          <p
            style={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '17px',
              color: T.textMedium,
              margin: 0,
            }}>
            <Trans>Having trouble?</Trans>{' '}
            <a href="mailto:support@wsocial.eu" className="LinkNew">
              <Trans>Contact support</Trans>
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
